import { FC, useContext, useEffect, useCallback } from "react";
import { Typography } from "@mui/material";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useDeleteLocalization from "src/Pages/Company/OrganizationStructure/api/useDeleteLocalization";
import __ from "src/utils/Translations";

import { Localization } from "src/Pages/Company/OrganizationStructure/Localizations/localizations.models";

interface DeleteLocalizationModalProps {
  companyId: string;
  structureId: string;
  localization: Localization;
  onClose: () => void;
}

const DeleteLocalizationModal: FC<DeleteLocalizationModalProps> = ({
  companyId,
  structureId,
  localization,
  onClose,
}) => {
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const { mutate: deleteLocalization, isPending } = useDeleteLocalization(
    companyId,
    structureId
  );

  const label = [
    localization.city,
    localization.streetAddress,
    localization.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
  const { employeesNumber } = localization;

  const handleClose = useCallback(() => {
    resetModal();
    onClose();
  }, [resetModal, onClose]);

  const handleDelete = useCallback(() => {
    deleteLocalization(localization.id, { onSuccess: handleClose });
  }, [deleteLocalization, localization.id, handleClose]);

  useEffect(() => {
    setModal({
      open: true,
      title: __("Usunąć lokalizację?"),
      maxWidth: 628,
      disableExitButton: true,
      disableOutsideClick: true,
      customElements: (
        <>
          <Typography sx={{ mt: 2 }}>
            {__("Lokalizacja ")}
            <span style={{ fontWeight: 600 }}>„{label}"</span>
            {__(" zostanie trwale usunięta z systemu.")}
          </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {__("Tej operacji nie można cofnąć.")}
          </Typography>
          {employeesNumber > 0 && (
            <Typography sx={{ mt: 2 }}>
              {__("Ta lokalizacja jest obecnie ")}
              <span style={{ fontWeight: 600 }}>
                {__("przypisana do {0} {1}", [
                  String(employeesNumber),
                  employeesNumber === 1 ? __("pracownika") : __("pracowników"),
                ])}
              </span>
              .<br />
              {__(
                "Po usunięciu stracą ją i będą wymagać ponownego przypisania."
              )}
            </Typography>
          )}
        </>
      ),
      actions: [
        {
          label: __("Anuluj"),
          variant: "outlined" as const,
          callbackFn: handleClose,
        },
        {
          label: __("Usuń"),
          variant: "contained" as const,
          color: "error" as const,
          callbackFn: handleDelete,
          loading: isPending,
        },
      ],
    });
  }, [setModal, handleClose, handleDelete, isPending, label, employeesNumber]);

  useEffect(
    () => () => {
      resetModal();
    },
    [resetModal]
  );

  return null;
};

export default DeleteLocalizationModal;
